import {
  AckResolveHistory,
  AckResolveStatus,
  AlertAttemptResponse,
  fetchIncidentHistory,
  getIncident,
  IncidentDetailResponse,
  IncidentPageResponse,
} from "@/api/incident";
import { MemberNameMapResponse } from "@/api/member";
import { useMemberNameMap } from "@/shared/hooks/useMemberNameMap";
import { useTeamStore } from "@/app/store/useTeamStore";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

// ── helpers ───────────────────────────────────────────────────────────────────

function getMemberName(
  memberId: number | null | undefined,
  members: MemberNameMapResponse[],
): string {
  if (memberId == null) return "-";
  return members.find((m) => m.id === memberId)?.name ?? "알 수 없음";
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${mo}-${day} ${h}:${mi}:${s}`;
}

function getHistory(
  history: AckResolveHistory[],
  status: AckResolveStatus,
) {
  return history.find((h) => h.status === status) ?? null;
}

/** 종결 엔트리: 사람이 resolve 한 행이 우선, 없으면 시스템 자동 만료(expired) 행 (SPT-4536) */
function getClosedEntry(history: AckResolveHistory[]) {
  return getHistory(history, "resolved") ?? getHistory(history, "expired");
}

/** 종결 처리자 표기. expired 는 member_id 가 null 인 시스템 행이다. */
function getCloserName(
  entry: AckResolveHistory | null,
  members: MemberNameMapResponse[],
): string {
  if (!entry) return "-";
  if (entry.status === "expired") return "시스템 (자동 만료)";
  return getMemberName(entry.member_id, members);
}

// ── AlertDetailModal ──────────────────────────────────────────────────────────

interface AlertDetailModalProps {
  incidentId: number | null;
  teamId: number;
  members: MemberNameMapResponse[];
  onClose: () => void;
}

function AlertDetailModal({
  incidentId,
  teamId,
  members,
  onClose,
}: AlertDetailModalProps) {
  const { data: detail, isError: isDetailError } = useQuery<IncidentDetailResponse>({
    queryKey: ["incident-detail", teamId, incidentId],
    queryFn: () => getIncident(teamId, incidentId!),
    enabled: incidentId != null,
  });

  return (
    <Dialog open={incidentId != null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <DialogTitle>알람 상세</DialogTitle>
            <Button variant="outline" size="sm" onClick={onClose}>
              닫기
            </Button>
          </div>
        </DialogHeader>

        {isDetailError ? (
          <p className="text-sm text-destructive py-4">상세 정보를 불러오는 중 오류가 발생했습니다.</p>
        ) : !detail ? (
          <p className="text-sm text-muted-foreground py-4">불러오는 중…</p>
        ) : (
          <div className="space-y-4">
            {/* title */}
            <div>
              <p className="text-xs text-muted-foreground">알람 제목</p>
              <p className="text-sm font-medium mt-0.5">{detail.title}</p>
            </div>

            {/* attempts table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-4 py-2 font-semibold">채널</th>
                    <th className="text-left px-4 py-2 font-semibold">발송 시각</th>
                    <th className="text-left px-4 py-2 font-semibold">Ack 상태</th>
                    <th className="text-left px-4 py-2 font-semibold">Ack 처리자</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.alert_attempts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-4 text-center text-muted-foreground"
                      >
                        알림 발송 내역이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    detail.alert_attempts.map(
                      (attempt: AlertAttemptResponse) => {
                        const ackedHistory = detail.ack_resolve_history.find(
                          (h) =>
                            h.status === "acked" &&
                            h.member_id === attempt.member_id,
                        );
                        const isAcked = attempt.status === "acked";
                        return (
                          <tr key={attempt.id} className="border-b last:border-0">
                            <td className="px-4 py-2">{attempt.channel}</td>
                            <td className="px-4 py-2">{fmtDate(attempt.created_at)}</td>
                            <td className="px-4 py-2">
                              {isAcked ? "확인됨" : "미확인"}
                            </td>
                            <td className="px-4 py-2">
                              {isAcked && ackedHistory
                                ? getMemberName(ackedHistory.member_id, members)
                                : "-"}
                            </td>
                          </tr>
                        );
                      },
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const PAGE_WINDOW = 5;

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function Pagination({ page, totalPages, onChange }: PaginationProps) {
  // 현재 페이지를 가운데 둔 최대 PAGE_WINDOW 개 번호
  const start = Math.max(1, Math.min(page - Math.floor(PAGE_WINDOW / 2), totalPages - PAGE_WINDOW + 1));
  const pages = Array.from({ length: Math.min(PAGE_WINDOW, totalPages) }, (_, i) => start + i);

  return (
    <nav className="flex items-center justify-center gap-1" aria-label="페이지 이동">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        이전
      </Button>
      {pages.map((p) => (
        <Button
          key={p}
          variant={p === page ? "default" : "ghost"}
          size="sm"
          aria-current={p === page ? "page" : undefined}
          onClick={() => onChange(p)}
        >
          {p}
        </Button>
      ))}
      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        다음
      </Button>
    </nav>
  );
}

// ── AlertHistoryPage ──────────────────────────────────────────────────────────

export function AlertHistoryPage() {
  const { selectedTeam } = useTeamStore();
  const teamId = selectedTeam?.id;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSelectedId(null);
    setPage(1);
  }, [teamId]);

  // queryKey 가 ["incidents", teamId] 로 시작해 ack/resolve 후 invalidate 대상에 포함된다
  const { data, isLoading, isError, isPlaceholderData } = useQuery<IncidentPageResponse>({
    queryKey: ["incidents", teamId, "history", page],
    queryFn: () => fetchIncidentHistory(teamId!, page, PAGE_SIZE),
    enabled: !!teamId,
    // 같은 팀 안의 페이지 이동에서만 이전 페이지를 유지 (팀 변경 시 다른 팀 이력이 보이지 않도록)
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === teamId ? previousData : undefined,
  });
  const incidents = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 마지막 페이지가 비면(이력 정리 등) 실제 마지막 페이지로 이동
  useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  const { nameMap, isError: isMembersError } = useMemberNameMap();
  const members = useMemo(() => Array.from(nameMap.values()), [nameMap]);

  if (!teamId) {
    return (
      <div className="p-6 text-muted-foreground text-sm">
        팀을 먼저 선택해주세요.
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold">인시던트 발생 이력</h1>
        <p className="text-sm text-muted-foreground">{selectedTeam?.name}</p>
      </div>

      {isMembersError && (
        <p className="text-xs text-destructive mb-3">멤버 정보를 불러오지 못했습니다. 일부 이름이 표시되지 않을 수 있습니다.</p>
      )}

      {isError ? (
        <p className="text-sm text-destructive">알림 내역을 불러오는 중 오류가 발생했습니다.</p>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : incidents.length === 0 ? (
        <div className="border border-dashed rounded-lg p-10 text-center text-sm text-muted-foreground">
          알림 내역이 없습니다.
        </div>
      ) : (
        // 새 페이지 로딩 중엔 이전 페이지 행을 흐리게 표시해 현재 페이지 결과로 오인하지 않게 한다
        <div
          className={`border rounded-lg overflow-hidden transition-opacity ${isPlaceholderData ? "opacity-50 pointer-events-none" : ""}`}
          aria-busy={isPlaceholderData}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th scope="col" className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  발생 시각
                </th>
                <th scope="col" className="text-left px-4 py-3 font-semibold">제목</th>
                <th scope="col" className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  출처
                </th>
                <th scope="col" className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  Ack 시각
                </th>
                <th scope="col" className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  Ack 처리자
                </th>
                <th scope="col" className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  Resolve 시각
                </th>
                <th scope="col" className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                  Resolve 처리자
                </th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((incident) => {
                const ackedEntry = getHistory(
                  incident.ack_resolve_history,
                  "acked",
                );
                const resolvedEntry = getClosedEntry(incident.ack_resolve_history);

                return (
                  <tr
                    key={incident.id}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {fmtDate(incident.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="w-full text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        // pointer-events-none 은 키보드 실행을 막지 못하므로 이전 페이지 행은 비활성화한다
                        disabled={isPlaceholderData}
                        onClick={() => setSelectedId(incident.id)}
                      >
                        {incident.title}
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {incident.source ?? "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {fmtDate(incident.acked_at)}
                    </td>
                    <td className="px-4 py-3">
                      {getMemberName(ackedEntry?.member_id, members)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {fmtDate(resolvedEntry?.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {getCloserName(resolvedEntry, members)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isError && total > 0 && (
        <div className="relative mt-4 flex items-center justify-center">
          <span className="absolute left-0 text-sm text-muted-foreground">총 {total}건</span>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}

      <AlertDetailModal
        incidentId={selectedId}
        teamId={teamId}
        members={members}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
